import {
  FaLaptopCode,
  FaMobileAlt,
  FaRocket,
  FaShieldAlt,
  FaSearch,
  FaServer,
} from "react-icons/fa";
import { Link } from "react-router-dom";

export default function StaticWebsite() {
  const features = [
    {
      icon: <FaRocket size={35} />,
      title: "Fast Performance",
      desc: "Lightning fast loading speed for better user experience.",
    },
    {
      icon: <FaMobileAlt size={35} />,
      title: "Responsive Design",
      desc: "Perfectly works on Mobile, Tablet and Desktop.",
    },
    {
      icon: <FaShieldAlt size={35} />,
      title: "Secure Website",
      desc: "No database means fewer security risks.",
    },
    {
      icon: <FaSearch size={35} />,
      title: "SEO Friendly",
      desc: "Optimized structure for better Google ranking.",
    },
    {
      icon: <FaLaptopCode size={35} />,
      title: "Modern UI",
      desc: "Professional and attractive website design.",
    },
    {
      icon: <FaServer size={35} />,
      title: "Easy Hosting",
      desc: "Deploy easily on Netlify, Vercel or GitHub Pages.",
    },
  ];

  return (
    <div 
      className="bg-[#030712] min-h-screen text-white pt-20 overflow-hidden selection:bg-cyan-500 selection:text-white"
      style={{ fontFamily: "emoji" }}
    >


      {/* HERO */}
      <section className="relative min-h-[70vh] flex items-center justify-center pt-10 border-b border-white/5 overflow-hidden">
        {/* Animated Grid Background */}
        <div className="absolute inset-0 z-0">
          <div className="absolute top-1/4 right-1/4 w-[300px] md:w-[500px] h-[300px] md:h-[500px] bg-cyan-500/10 rounded-full blur-[100px] md:blur-[150px]" />
          <div className="absolute bottom-1/4 left-1/4 w-[250px] md:w-[400px] h-[250px] md:h-[400px] bg-violet-600/10 rounded-full blur-[100px] md:blur-[150px]" />
        </div>

        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 md:gap-16 items-center px-4 sm:px-6 relative z-10 w-full py-12 md:py-0">
          <div className="text-center md:text-left flex flex-col items-center md:items-start">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#0EA5E9]/30 bg-[#0EA5E9]/10 text-[#0EA5E9] text-xs md:text-sm font-bold tracking-wider uppercase mb-6 md:mb-8 backdrop-blur-md shadow-lg">
              Static Website
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black text-white leading-tight mb-6 md:mb-8">
              Fast & Secure<br/>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0EA5E9] to-[#06B6D4]">
                Web Presence
              </span>
            </h1>
            <h3 className="text-xl sm:text-2xl font-semibold text-transparent bg-clip-text bg-gradient-to-r from-[#0EA5E9] to-[#06B6D4] mb-4 tracking-wide">
              Empowering Your Business with Lightning-Fast Digital Solutions
            </h3>
            <p className="text-gray-300 text-base md:text-lg leading-relaxed font-light mb-8 md:mb-10 max-w-lg">
              We create fast, secure, responsive and professional static websites for businesses, startups and portfolios.
            </p>
            <Link to="/contact">
              <button className="px-8 py-3.5 md:py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-2xl font-bold tracking-wide hover:scale-105 transition-all duration-300 shadow-[0_0_30px_rgba(14,165,233,0.3)] w-full sm:w-auto">
                Get Started
              </button>
            </Link>
          </div>
          <div className="relative mt-10 md:mt-0 px-4 md:px-0">
            <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/20 to-violet-500/20 rounded-2xl blur-xl" />
            <img
              src="https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=900"
              className="relative rounded-2xl border border-white/10 shadow-2xl w-full"
              alt="Hero Illustration"
            />
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="py-20 md:py-24 relative z-10 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 md:mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#0EA5E9]/30 bg-[#0EA5E9]/10 text-[#0EA5E9] text-xs font-bold tracking-widest uppercase mb-4 shadow-lg">
              Core Benefits
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black tracking-tight text-white mb-4 md:mb-6">
              Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0EA5E9] to-[#06B6D4]">Features</span>
            </h2>
            <p className="text-transparent bg-clip-text bg-gradient-to-r from-[#0EA5E9] to-[#06B6D4] text-base md:text-lg max-w-2xl mx-auto font-light">
              Everything you need for a blazing fast, secure, and SEO-optimized website.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {features.map((item, index) => (
              <div
                key={index}
                className="group relative overflow-hidden rounded-[2rem] border border-white/[0.06] hover:border-cyan-500/30 bg-[#0A0F1F]/50 backdrop-blur-sm transition-all duration-500 hover:-translate-y-1 md:hover:-translate-y-2 p-6 md:p-8 text-center sm:text-left flex flex-col items-center sm:items-start"
              >
                <div className="absolute -inset-[1px] rounded-[2rem] bg-gradient-to-b from-cyan-500/0 via-cyan-500/20 to-violet-500/0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 blur-[1px]" />
                <div className="relative z-10 w-full">
                  <div className="w-12 h-12 md:w-14 md:h-14 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center mb-5 md:mb-6 group-hover:scale-110 group-hover:-rotate-6 transition-all duration-500 text-cyan-400 mx-auto sm:mx-0">
                    {item.icon}
                  </div>
                  <h3 className="text-lg md:text-xl font-bold text-white mb-2 md:mb-3">
                    {item.title}
                  </h3>
                  <p className="text-gray-400 text-sm md:text-base leading-relaxed font-light">
                    {item.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="py-20 md:py-24 relative z-10 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">

          {/* Title — mobile center */}
          <div className="text-center mb-10 md:mb-0">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#0EA5E9]/30 bg-[#0EA5E9]/10 text-[#0EA5E9] text-xs font-bold tracking-widest uppercase mb-4 shadow-lg">
              Why Us
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-white mb-3">
              Why Choose{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0EA5E9] to-[#06B6D4]">
                Static Website?
              </span>
            </h2>
            <p className="text-transparent bg-clip-text bg-gradient-to-r from-[#0EA5E9] to-[#06B6D4] text-base md:text-lg max-w-2xl mx-auto font-light mt-4">
              A modern approach to web development that guarantees superior performance, security, and scalability.
            </p>
          </div>

          <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 items-center mt-10">
            {/* Image */}
            <div className="relative w-full lg:w-1/2">
              <div className="absolute inset-0 bg-gradient-to-br from-violet-500/20 to-cyan-500/20 rounded-2xl blur-xl" />
              <img
                src="https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=900"
                className="relative rounded-2xl border border-white/10 shadow-2xl w-full"
                alt="Why Choose Static Website"
              />
            </div>

            {/* List */}
            <div className="w-full lg:w-1/2 flex flex-col gap-4">
              {[
                { label: "Fast Loading Speed",     sub: "Pages load in milliseconds — no server delay." },
                { label: "Affordable Development", sub: "Lower cost to build and maintain." },
                { label: "Better SEO",             sub: "Pre-rendered pages rank higher on Google." },
                { label: "Mobile Friendly",        sub: "Looks great on any device or screen size." },
                { label: "Highly Secure",          sub: "No database = far fewer vulnerabilities." },
                { label: "Easy Maintenance",       sub: "Simple updates without complex backends." },
              ].map((item, i) => (
                <div
                  key={i}
                  className="flex items-start gap-4 bg-[#0A0F1F]/50 border border-white/[0.06] rounded-2xl px-5 py-4 hover:border-cyan-500/30 transition-all duration-300"
                >
                  <div className="w-8 h-8 rounded-xl bg-cyan-500/20 border border-cyan-500/30 flex items-center justify-center shrink-0 text-cyan-400 text-sm font-bold mt-0.5">
                    ✔
                  </div>
                  <div>
                    <p className="font-bold text-white text-sm md:text-base leading-snug">{item.label}</p>
                    <p className="text-gray-400 text-xs md:text-sm mt-0.5">{item.sub}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>


      {/* TECHNOLOGIES */}
      <section className="py-20 md:py-24 relative z-10 border-b border-white/5 bg-[#0A0F1F]/20 overflow-hidden">
        {/* Background glow */}
        <div className="absolute inset-0 z-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] md:w-[600px] h-[300px] md:h-[600px] bg-gradient-to-r from-cyan-500/10 to-violet-500/10 rounded-full blur-[80px] md:blur-[120px] animate-[spin_10s_linear_infinite]" />
        </div>
        
        <div className="max-w-6xl mx-auto text-center px-4 sm:px-6 relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 md:py-2 rounded-full border border-[#0EA5E9]/30 bg-[#0EA5E9]/10 text-[#0EA5E9] text-xs md:text-sm font-bold tracking-wider uppercase mb-4 backdrop-blur-md shadow-lg">
            Tech Stack
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-black mb-4 text-white tracking-tight">
            Built with <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0EA5E9] to-[#06B6D4]">Modern Tech</span>
          </h2>
          <p className="text-transparent bg-clip-text bg-gradient-to-r from-[#0EA5E9] to-[#06B6D4] text-base md:text-lg max-w-2xl mx-auto font-light mb-12 md:mb-16">
            We use the latest technologies to ensure your website is incredibly fast, responsive, and future-proof.
          </p>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-6">
            {[
              { name: "HTML5", color: "from-orange-500 to-red-500", delay: "" },
              { name: "CSS3", color: "from-blue-500 to-cyan-500", delay: "delay-100" },
              { name: "JavaScript", color: "from-yellow-400 to-yellow-600", delay: "delay-200" },
              { name: "React", color: "from-cyan-400 to-blue-500", delay: "delay-300" },
              { name: "Tailwind", color: "from-teal-400 to-cyan-500", delay: "delay-400" },
              { name: "Bootstrap", color: "from-purple-500 to-indigo-500", delay: "delay-500" }
            ].map((tech, idx) => (
              <div
                key={idx}
                className={`group relative h-32 md:h-40 rounded-2xl md:rounded-[2rem] bg-[#0A0F1F]/60 backdrop-blur-xl border border-white/10 hover:border-white/30 transition-all duration-500 flex flex-col items-center justify-center overflow-hidden hover:-translate-y-2 hover:shadow-[0_0_30px_rgba(14,165,233,0.15)] cursor-default animate-float-tech ${tech.delay}`}
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${tech.color} opacity-0 group-hover:opacity-10 transition-opacity duration-500`} />
                <div className="relative z-10 flex flex-col items-center">
                  <div className={`w-10 h-10 md:w-14 md:h-14 rounded-xl md:rounded-2xl bg-gradient-to-br ${tech.color} p-[1px] mb-3 md:mb-4 group-hover:scale-110 transition-transform duration-500 shadow-lg`}>
                    <div className="w-full h-full bg-[#030712] rounded-xl md:rounded-2xl flex items-center justify-center">
                      <div className={`w-4 h-4 md:w-6 md:h-6 rounded-full bg-gradient-to-br ${tech.color} blur-[10px] absolute`} />
                      <span className="font-black text-white text-lg md:text-2xl z-10">{tech.name[0]}</span>
                    </div>
                  </div>
                  <span className="font-bold text-gray-300 group-hover:text-white transition-colors text-xs md:text-sm">
                    {tech.name}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-20 md:py-24 relative z-10 border-b border-white/5">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 md:mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#0EA5E9]/30 bg-[#0EA5E9]/10 text-[#0EA5E9] text-xs font-bold tracking-widest uppercase mb-4 shadow-lg">
              Methodology
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-white mb-4">
              Development <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0EA5E9] to-[#06B6D4]">Process</span>
            </h2>
            <p className="text-transparent bg-clip-text bg-gradient-to-r from-[#0EA5E9] to-[#06B6D4] text-base md:text-lg max-w-2xl mx-auto font-light">
              A streamlined, hassle-free process from concept to deployment.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 md:gap-6">
            {[
              "Planning",
              "Design",
              "Development",
              "Testing",
              "Launch",
            ].map((step, index) => (
              <div
                key={index}
                className="bg-[#0A0F1F]/50 rounded-2xl md:rounded-[2rem] border border-white/10 p-6 md:p-8 text-center hover:-translate-y-1 md:hover:-translate-y-2 transition-all duration-300 hover:border-cyan-500/30 flex lg:flex-col items-center lg:items-center justify-start lg:justify-center gap-4 lg:gap-0"
              >
                <div className="w-12 h-12 md:w-14 md:h-14 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-xl md:rounded-2xl text-white flex justify-center items-center lg:mx-auto lg:mb-6 font-bold shadow-lg shrink-0">
                  {index + 1}
                </div>
                <h3 className="font-bold text-gray-200 text-lg md:text-base">
                  {step}
                </h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 relative z-10 overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] md:w-[600px] h-[300px] md:h-[600px] bg-cyan-500/10 rounded-full blur-[100px] md:blur-[150px] pointer-events-none" />
        <div className="text-center text-white relative z-10 px-4 sm:px-6">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black mb-6 md:mb-8 tracking-tight">
            Ready To Build Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0EA5E9] to-[#06B6D4]">Website?</span>
          </h2>
          <p className="text-transparent bg-clip-text bg-gradient-to-r from-[#0EA5E9] to-[#06B6D4] text-base md:text-xl font-light max-w-2xl mx-auto mb-8 md:mb-10">
            Let's create a modern and responsive static website for your business.
          </p>
          <Link to="/contact">
            <button className="px-8 md:px-10 py-3.5 md:py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-2xl font-bold tracking-wide hover:scale-105 transition-all duration-300 shadow-[0_0_30px_rgba(14,165,233,0.3)] w-full sm:w-auto">
              Contact Us
            </button>
          </Link>
        </div>
      </section>

    </div>
  );
}